import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import moment from "moment";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { IForsorjningskalla } from "~app/common/models/forsorjningskalla";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ILeveransrad } from "../leverantorsorder/models/leverantororder-models";
import { KundorderListRequestDTO } from "../models/KundorderListRequestDTO";
import { KundorderListResponseDTO } from "../models/KundorderListResponseDTO";
import { KundorderMottagningsplatsTypeaheadRequestDTO } from "./models/KundorderMottagningsplatsTypeaheadRequestDTO";
import {
    IAlternativtHandelssortiment,
    IKundorderDetailGrunddata,
    IKundorderDetailKundorder,
    IKundorderDetailKundorderrad,
    IUppdatateraPeriodLeveransraderResponse
} from "./models/kundorder-detail-model";

@Injectable({
    providedIn: "root"
})
export class KundorderService {
    alternativaHandelssortimentErrors$ = new BehaviorSubject<string[]>(null);

    private readonly apiClientService = inject(ApiClientService);

    get(id: number): Observable<IKundorderDetailKundorder> {
        return this.apiClientService.get<IKundorderDetailKundorder>(`kundorder/${id}`);
    }

    getActiveKundorderRad(rader: IKundorderDetailKundorderrad[]) {
        return rader.filter(kundorderrad => !kundorderrad.isDeleted);
    }

    getBalans(leveransrader: ILeveransrad[], kvantitet: number) {
        if (leveransrader && leveransrader.length) {
            return this.roundHalfAwayFromZero(this.sumTransporteratLeveransrad(leveransrader) - kvantitet + 0);
        }
        return 0;
    }

    getGrunddata(): Observable<IKundorderDetailGrunddata> {
        return this.apiClientService.get<IKundorderDetailGrunddata>("/Kundorder/basdata");
    }

    getKommentarForKundorderrad(kundorderradId: number): Observable<string> {
        return this.apiClientService.get<string>("/kundorderrad/GetKommentar", false, { kundorderradId: kundorderradId });
    }

    getLeveransraderForPeroid(leveransrader: ILeveransrad[], dateFrom: Date, dateTo: Date): Observable<IUppdatateraPeriodLeveransraderResponse> {
        const request = {} as any;
        request.leveransrader = leveransrader;
        request.periodStart = dateFrom;
        request.periodSlut = dateTo;

        return this.apiClientService.post<any, any>("Kundorder/UppdateraPeriodLeveransrader", request);
    }

    getPeriodFrom(rader: IKundorderDetailKundorderrad[]) {
        const kundorderrader = this.getActiveKundorderRad(rader);
        return Math.min.apply(null, kundorderrader.filter(r => r.periodFrom !== undefined).map(kundorderrad => new Date(kundorderrad.periodFrom)));
    }

    getPeriodTom(rader: IKundorderDetailKundorderrad[]) {
        const kundorderrader = this.getActiveKundorderRad(rader);
        return Math.max.apply(null, kundorderrader.filter(r => r.periodTom !== undefined).map(kundorderrad => new Date(kundorderrad.periodTom)));
    }

    getSummaLeveransrader(rader: IKundorderDetailKundorderrad[]): number {
        if (rader && rader.length) {
            return rader
                .reduce<number>((summa, kundorderrad) => summa + (kundorderrad.isDeleted ? 0 : this.getSummaLeveransraderFromKundorderrad(kundorderrad)), 0);
        }
        return 0;
    }

    search(request: KundorderListRequestDTO): Observable<KundorderListResponseDTO> {
        request.kundordertyp = "KUND";
        return this.apiClientService.post<KundorderListRequestDTO, KundorderListResponseDTO>("kundorder/search", request);
    }

    searchForAvtalsansvarigTypeahead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClientService.post<any, ITypeAheadResult[]>("typeahead/anvandare", { query: input }, false, null, ApiClientService.JsonHeader);
    }

    searchForKundTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClientService.post<any, ITypeAheadResult[]>(`typeahead/kund`, `"${input}"`, false, null, ApiClientService.JsonHeader);
    }

    searchForMottagningsplatsTypeahead(input: string): Observable<ITypeAheadResult[]> {
        const request = new KundorderMottagningsplatsTypeaheadRequestDTO();
        request.input = input;
        request.kundordertyp = "";
        return this.apiClientService.post<any, ITypeAheadResult[]>(`typeahead/mottagningsplats`, request, false, null, ApiClientService.JsonHeader);
    }

    searchForsorjningskalla(request: any, exclude: (fk: IForsorjningskalla) => boolean): Observable<any> {
        const include = (x: IForsorjningskalla) => !exclude(x);

        return this.apiClientService.post<any, IForsorjningskalla[]>("typeahead/forsorjningskallor/ByHandelssortiment", request)
            .pipe(
                map(result => result.filter(include)));
    }

    validateAlternativaHandelssortiment(alternativtHandelssortiment: IAlternativtHandelssortiment[], kundorderradHandelssortimentId: number) {
        const errorMessages = [];
        if (alternativtHandelssortiment.length === 0) {
            return;
        }
        let hasInvalidEndastCkError = false;
        let hasInvalidEjEndastCkError = false;
        _.chain(alternativtHandelssortiment)
            .map(ah => ({
                inmatningshandelssortimentId: ObjectUtils.hasValue(ah.inmatningshandelssortiment) ?
                    ah.inmatningshandelssortiment.id : ah.beraknatInmatningshandelssortiment?.id,
                endastCentralaKop: ah.endastCentralaKop,
                handelssortimentId: ah.handelssortimentId
            }))
            .groupBy(p => p.handelssortimentId)
            .forEach(val => {
                // Grupperat per HS.
                // 1. Inom Endast CK == true => Dubbletter tillåtna på HS-nivå, men ej på inmätningshandelssortiment
                hasInvalidEndastCkError ||= _.chain(val.filter(s => s.endastCentralaKop)).groupBy(x => x.inmatningshandelssortimentId).filter(x => x.length > 1).size().value() > 0;
                // 2. Inom Endast CK == false => Dubbletter aldrig tillåtna, inte ens på HS-nivå
                hasInvalidEjEndastCkError ||= _.chain(val.filter(s => !s.endastCentralaKop)).size().value() > 1;
            }).value();

        if (hasInvalidEndastCkError || hasInvalidEjEndastCkError) {
            errorMessages.push("Samma handelssortiment får anges endast om centralaköp valt samt att inmätningshandelssortiment skiljer");
        }

        if (alternativtHandelssortiment.some(x => x.handelssortimentId === kundorderradHandelssortimentId && !x.endastCentralaKop)) {
            errorMessages.push("Samma alternativt handelssortiment får endast väljas om endast centralaköp är markerat");
        }

        this.alternativaHandelssortimentErrors$.next(errorMessages);
    }

    private getSummaLeveransraderFromKundorderrad(kundorderrad: IKundorderDetailKundorderrad): number {
        if (kundorderrad.leveransrader && kundorderrad.leveransrader.length) {
            return kundorderrad.leveransrader
                .reduce<number>((summa, leveransrad) => ((leveransrad.kvantitet && leveransrad.kvantitet > 0) ? (summa + leveransrad.kvantitet) : summa), 0);
        }
        return 0;
    }

    private roundHalfAwayFromZero(value: number) {
        if (value >= 0) {
            return Math.round(value);
        }
        return (value % 0.5 === 0) ? Math.floor(value) : Math.round(value);
    }

    private sumTransporteratLeveransrad(leveransrader: ILeveransrad[]): number {
        const currMonth = moment().startOf("month");
        const summa = leveransrader
            .map(x => {
                const leveransMonth = moment(x.datum).startOf("month");
                if (leveransMonth < currMonth) {
                    return x.transporteratKvantitet;
                } else {
                    return x.kvantitet;
                }
            })
            .reduce((s, k) => s + k, 0);
        return this.roundHalfAwayFromZero(summa);
    }
}
